import { RegionGeoJson } from '@business/entities/region';

export interface LatLng {
  lat: number;
  lng: number;
}

/** Outer ring of every polygon in the boundary, as Google paths ([lng, lat] → {lat, lng}). */
export function geoJsonToPaths(geoJson: RegionGeoJson | null | undefined): LatLng[][] {
  const geometry = geoJson?.geometry;
  if (!geometry?.coordinates) return [];
  const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.type === 'MultiPolygon' ? geometry.coordinates : [];
  return polygons
    .filter((polygon: any) => Array.isArray(polygon?.[0]))
    .map((polygon: any) => openRing(polygon[0].map(([lng, lat]: number[]) => ({ lat: Number(lat), lng: Number(lng) }))));
}

/** A Polygon Feature from a Google path; the ring is closed as GeoJSON requires. */
export function pathToGeoJson(path: LatLng[]): RegionGeoJson {
  const coordinates = path.map(p => [p.lng, p.lat]);
  const first = coordinates[0];
  const last = coordinates[coordinates.length - 1];
  if (first && (first[0] !== last[0] || first[1] !== last[1])) coordinates.push(first);
  return { type: 'Feature', geometry: { type: 'Polygon', coordinates: [coordinates] }, properties: {} };
}

export function isEditableBoundary(geoJson: RegionGeoJson | null | undefined) {
  return geoJsonToPaths(geoJson).length === 1;
}

// Google closes polygons itself; a repeated last point would show as a doubled, draggable corner.
function openRing(ring: LatLng[]) {
  const first = ring[0];
  const last = ring[ring.length - 1];
  return ring.length > 1 && first.lat === last.lat && first.lng === last.lng ? ring.slice(0, -1) : ring;
}

/** Where a territory's name sits: the average corner of its largest part. */
export function labelPoint(paths: LatLng[][]): LatLng | null {
  const largest = paths.reduce<LatLng[]>((best, path) => (path.length > best.length ? path : best), []);
  if (!largest.length) return null;
  const sum = largest.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), { lat: 0, lng: 0 });
  return { lat: sum.lat / largest.length, lng: sum.lng / largest.length };
}
